import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import apiClient from '../../../api/client';
import { AI_ENDPOINTS } from '../../../api/endpoints';
import type { Question } from '../../../types';

export type AiOutcome = 'valid' | 'needs_more_info' | 'invalid';

export interface AiProcessedValue {
  answer: string;
  score: number;
  outcome: AiOutcome;
}

interface AiProcessedRendererProps {
  question: Question;
  selectedValue: AiProcessedValue | undefined;
  onSelect: (value: AiProcessedValue) => void;
  onProcessingComplete?: () => void;
}

function AiProcessedRenderer({
  question,
  selectedValue,
  onSelect,
  onProcessingComplete,
}: AiProcessedRendererProps): JSX.Element {
  const [answer, setAnswer] = useState<string>(selectedValue?.answer ?? '');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<AiProcessedValue | null>(selectedValue ?? null);
  const [error, setError] = useState<string | null>(null);

  const systemPrompt = question.aiConfig?.systemPrompt ?? '';

  const handleSubmit = async (): Promise<void> => {
    if (!answer.trim()) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await apiClient.post<{ score: number; outcome: AiOutcome }>(
        AI_ENDPOINTS.PROCESS_ANSWER,
        { answer: answer.trim(), systemPrompt },
      );

      const processed: AiProcessedValue = {
        answer: answer.trim(),
        score: response.data.score,
        outcome: response.data.outcome,
      };

      setResult(processed);
      onSelect(processed);
      onProcessingComplete?.();
    } catch {
      setError('Unable to process your answer. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const inputSx = {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e0e0e0', borderRadius: '12px' },
    '&:hover fieldset': { borderColor: '#b0b0b0' },
    '&.Mui-focused fieldset': { borderColor: '#00A9B5' },
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="body2"
        sx={{ mb: 3, fontStyle: 'italic', color: '#888', fontSize: '0.82rem' }}
      >
        Please describe your answer in detail — your response will be evaluated automatically.
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        value={answer}
        onChange={(e) => {
          setAnswer(e.target.value);
          setResult(null);
        }}
        placeholder="Type your answer here…"
        disabled={isProcessing}
        InputProps={{ sx: inputSx }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ flex: 1 }}>
            Your answer has been recorded.
          </Alert>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!answer.trim() || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: '#00A9B5',
            '&:hover': { bgcolor: '#008c96' },
            borderRadius: '8px',
            px: 3,
          }}
        >
          {isProcessing ? 'Processing…' : 'Submit Answer'}
        </Button>
      )}
    </Box>
  );
}

export default AiProcessedRenderer;
